import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import BarChart from "../../BarChart";
import HorizontalBarChart from "../../HorizontalBarChart";
import { SupplierOverviewResponse, SupplierByYear } from "../../../../types/suppliers";
import supplierTotal from "../../../../assets/images/supplierTotal.svg";
import supplierActive from "../../../../assets/images/supplierActive.svg";
import supplierRegion from "../../../../assets/images/supplierRegion.svg";
import supplierRegister from "../../../../assets/images/supplieRegister.svg";
import statusDraft from "../../../../assets/images/statusDraft.svg";
import statusSubmitted from "../../../../assets/images/statusSubmitted.svg";
import statusPending from "../../../../assets/images/statusPending.svg";
import statusInProgress from "../../../../assets/images/statusInProgress.svg";
import statusApproved from "../../../../assets/images/statusApproved.svg";
import statusWarning from "../../../../assets/images/statusWarning.svg";
import statusProtected from "../../../../assets/images/statusProtected.svg";
import styles from "./overviewDashboard.module.scss";

interface OverviewDashboardProps {
  overviewData: SupplierOverviewResponse;
}

interface CardConfig {
  key: string;
  icon: string;
}

const OverviewDashboard: React.FC<OverviewDashboardProps> = ({
  overviewData,
}) => {
  // Transform suppliersCountByYear to SupplierByYear[] format for BarChart
  const suppliersByYearData = useMemo((): SupplierByYear[] => {
    if (overviewData.suppliersCountByYear) {
      const { years, totalSuppliers, newSuppliers } = overviewData.suppliersCountByYear;
      return years.map((year, index) => ({
        year,
        total: totalSuppliers[index] || 0,
        new: newSuppliers[index] || 0,
      }));
    }
    return overviewData.suppliersByYear || [];
  }, [overviewData.suppliersCountByYear, overviewData.suppliersByYear]);
  const primaryCardConfigs: CardConfig[] = [
    { key: "totalSuppliers", icon: supplierTotal},
    { key: "activeSuppliers", icon: supplierActive},
    { key: "uaeRegionSuppliers", icon: supplierRegion},
    { key: "registeredSuppliersInSpecificYear", icon: supplierRegister},
  ];

  const renderPrimaryCards = () => {
    if (!overviewData.primaryCards) return null;

    return primaryCardConfigs.map((config, index) => {
      const card =
        overviewData.primaryCards[
          config.key as keyof typeof overviewData.primaryCards
        ];
      if (!card) return null;

      return (
        <Box key={index} className={styles.metricCard}>
          <Box className={styles.metricIconWrapper}>
            <Box
              className={styles.metricIcon}
            >
              <img src={config.icon} alt={config.key} />
            </Box>
          </Box>
          <Box className={styles.metricContent}>
            <Typography variant="h3" className={styles.metricValue}>
              {card.count}
            </Typography>
            <Typography variant="body2" className={styles.metricLabel}>
              {card.label}
            </Typography>
          </Box>
        </Box>
      );
    });
  };

  const renderStatusTypes = () => {
    if (!overviewData.statusTypes || overviewData.statusTypes.length === 0) {
      return null;
    }

    // Display status types as secondary cards
    return overviewData.statusTypes.slice(0, 7).map((statusType, index) => {
      const iconColors = "#F9FAFB";
      const icons = [
        statusDraft,
        statusSubmitted,
        statusPending,
        statusInProgress,
        statusApproved,
        statusWarning,
        statusProtected,
      ];

      return (
        <Box key={index} className={styles.secondaryMetricCard}>
          <Box
            className={styles.secondaryMetricIcon}
            style={{ backgroundColor: iconColors[index % iconColors.length] }}
          >
           <img
              src={icons[index % icons.length]}
              alt="status icon"
            />
          </Box>
          <Box className={styles.secondaryMetricContent}>
            <Typography variant="h5" className={styles.secondaryMetricValue}>
              {statusType.count}
            </Typography>
            <Typography variant="body2" className={styles.secondaryMetricLabel}>
              {statusType.label}
            </Typography>
          </Box>
        </Box>
      );
    });
  };

  return (
    <>
      <Box className={styles.primaryMetrics}>{renderPrimaryCards()}</Box>

      <Box className={styles.secondaryMetrics}>{renderStatusTypes()}</Box>

      <Box className={styles.chartsSection}>
        <BarChart
          title="Suppliers Count by Year"
          data={suppliersByYearData}
          xAxisKey="year"
          bars={[
            { dataKey: "total", color: "#455391", label: "Total Suppliers" },
            { dataKey: "new", color: "#00D2AA", label: "New Suppliers" },
          ]}
        />
        <HorizontalBarChart
          title="Due Diligence Status"
          data={overviewData.dueDiligenceStatus || []}
          nameKey="status"
          valueKey="count"
          barColor="#5b6b9e"
          valueLabel="suppliers"
        />
      </Box>
    </>
  );
};

export default OverviewDashboard;
